<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row justify="start" align="center">
      <v-col cols="4" md="3" class="py-2 my-0">
        <v-autocomplete
          v-model="form.particular"
          :items="feeTypes"
          outlined
          dense
          hide-details="auto"
          clearable
          multiple
          label="Particular"
          item-text="text"
          item-value="text"
        >
          <template v-slot:selection="{ item, index }">
            <div class="d-flex align-center">
              <v-chip v-if="index < 2" small class="mr-2" outlined>
                {{ item.text }}
              </v-chip>
              <span v-if="index === 2" class="text-caption ml-2">
                (+{{ form.particular.length - 2 }} others)
              </span>
            </div>
          </template>
        </v-autocomplete>
      </v-col>

      <v-col cols="4" md="3" class="py-0 my-0">
        <v-text-field
          v-model.trim="form.usn_id"
          label="USN"
          outlined
          dense
          hide-details="auto"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          outlined
          dense
          clearable
          hide-details="auto"
          :item-text="
            (item) =>
              `${item.degree.short_name} ${
                item.specialization ? `- ${item.specialization}` : ''
              }`
          "
          item-value="id"
          label="Programme"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.partner_id"
          :items="partners"
          outlined
          dense
          clearable
          hide-details="auto"
          item-text="name"
          item-value="id"
          label="Partner"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.status"
          :items="statuses"
          item-text="name"
          item-value="id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Status"
        ></v-autocomplete>
      </v-col>

      <v-col cols="4" md="3" class="d-flex justify-start">
        <v-btn
          class="accent white--text"
          :loading="loading"
          @click="init()"
          :disabled="loading"
        >
          Search
        </v-btn>
        <v-btn
          :disabled="!studentInvoices.length"
          :loading="downloadLoader"
          class="accent white--text ml-2"
          @click="download()"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            v-model="selectedApplicants"
            :headers="requestFeeHeaders"
            :items="studentInvoices"
            fixed-header
            item-key="stn"
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
            <template #[`item.document_amount_details`]="{ item }">
              <div
                v-if="item.document_amount_details?.some((doc) => doc.amount)"
                class="d-flex flex-wrap"
              >
                <v-chip
                  v-for="(doc, index) in item.document_amount_details"
                  :key="index"
                  class="ma-1"
                  small
                  variant="outlined"
                >
                  {{ doc.document }}
                </v-chip>
              </div>
            </template>
            <template #[`item.action`]="{ item }">
              <v-icon
                small
                class="mr-2"
                color="accent"
                @click="edit(item)"
                :disabled="item.status == 'Paid'"
              >
                mdi-pencil
              </v-icon>
            </template>
            <template #[`item.partner_id`]="{ item }">
              {{ getPartnerName(item.partner_id) }}
            </template>
            <template #[`item.receipt_number`]="{ item }">
              {{ item.receipt_number ? item.receipt_number : "-" }}
            </template>
            <template #[`item.transaction_id`]="{ item }">
              {{ item.transaction_id ? item.transaction_id : "-" }}
            </template>
            <template #[`item.transaction_date`]="{ item }">
              {{
                item.transaction_date ? formatDate(item.transaction_date) : "-"
              }}
            </template>
            <template #[`item.mobile_no`]="{ item }">
              {{ item.mobile_no ? item.mobile_no : "-" }}
            </template>
            <template #[`item.program_id`]="{ item }">
              {{ getProgramName(item.program_id) }}
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="examPaper" max-width="600px">
      <examPaperDialog
        v-if="examPaper"
        @closeDialog="(examPaper = false), (studentExamPapers = [])"
        :studentExamPapers="studentExamPapers"
      />
    </v-dialog>
    <v-dialog v-model="emailTrigger" max-width="800">
      <emailTriggerDialog
        v-if="emailTrigger"
        @closeDialog="(emailTrigger = false), (selectedApplicants = [])"
        @trigger="triggerPendingApplicantEmail"
        :selectedApplicants="selectedApplicants"
      />
    </v-dialog>
    <v-dialog v-model="amountChange" width="800" persistent scrollable>
      <amountChangeDialog
        v-if="amountChange"
        @closeDialog="amountChange = false"
        @save="(amountChange = false), init()"
        :invoice="requestPayment"
        :options_to_display="options_to_display"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import { requestFeeHeaders } from "@/constants/headers";
import examPaperDialog from "@/components/academicPayment/examPaper.vue";
import emailTriggerDialog from "@/components/academicPayment/emailTrigger.vue";
import amountChangeDialog from "@/components/academicPayment/amountChange.vue";

export default {
  components: { examPaperDialog, emailTriggerDialog, amountChangeDialog },

  data() {
    return {
      form: {},
      programs: [],
      partners: [],
      batches: [],
      statuses: [
        { id: "Completed", name: "Paid" },
        { id: "Approved", name: "Due" },
      ],
      loading: false,
      requestFeeHeaders,
      studentInvoices: [],
      selectedApplicants: [],
      feeTypes: [],
      miscellaneousFees: [],
      studentExamPapers: [],
      academicPayment: {},
      examPaper: false,
      amountChange: false,
      emailTrigger: false,
      rules,
      downloadLoader: false,
      requestPayment: {},
      options_to_display: ["Due Date"],
    };
  },

  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
  },

  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Request Fee" }]);
    this.init();
  },

  methods: {
    async init() {
      await Promise.all([
        this.getDocuments(),
        this.generateBatchList(),
        this.getMiscelleaneousFees(),
        this.getPartners(),
        this.getPrograms(),
      ]);
    },

    async generateBatchList() {
      try {
        const res = await this.$axios.get("/get_year_batch");
        this.batches = res.data.data;
      } catch (error) {
        console.log(error);
      }
    },

    async getMiscelleaneousFees() {
      try {
        const res = await this.$axios.get("/miscellaneous_fees");

        this.miscellaneousFees = res.data.data;

        this.feeTypes = this.miscellaneousFees
          .sort((a, b) => a.id - b.id)
          .map((f) => ({ id: f.id, text: f.particular }));

        this.feeTypes.push(
          { id: "search_fee", text: "Search Fee" },
          { id: "postal_fee", text: "Postal Fee" },
        );
      } catch (error) {
        console.log(error);
      }
    },

    async getDocuments() {
      this.loading = true;

      try {
        if (this.form.particular == "") delete this.form.particular;
        const queryString = this.objectToQueryParams({ ...this.form });
        const res = await this.$axios.get(
          `/student_document_requests_with_details?${queryString}`,
        );

        this.studentInvoices = res.data.data.sort((a, b) => {
          const dateA = a.transaction_date
            ? new Date(a.transaction_date.split("-").reverse().join("-"))
            : null;
          const dateB = b.transaction_date
            ? new Date(b.transaction_date.split("-").reverse().join("-"))
            : null;
          return (dateA || 0) - (dateB || 0);
        });
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    async getPrograms() {
      try {
        const res = await this.$axios.get("/programs");
        this.programs = res.data.data;
      } catch (error) {
        console.log(error);
      }
    },

    async getPartners() {
      try {
        const res = await this.$axios.get("/partners");
        this.partners = res.data.data;
      } catch (error) {
        console.log(error);
      }
    },

    objectToQueryParams(obj) {
      if (this.currentUser.partner_id != 1) {
        obj.partner_child_id = this.currentUser.partner_id;
      }

      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }

      if (obj.partner_id == 1) delete obj.partner_id;
      if (!obj?.types?.length) delete obj.types;

      return Object.keys(obj)
        .filter(
          (key) =>
            obj[key] !== undefined && obj[key] !== null && obj[key] !== "",
        )
        .map(
          (key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`,
        )
        .join("&");
    },

    getProgramName(id) {
      const program = this.programs.find((p) => p.id === id);
      return program
        ? program.degree.short_name +
            (program.specialization ? " - " + program.specialization : "")
        : "";
    },

    getPartnerName(id) {
      const partner = this.partners.find((partner) => partner.id === id);
      return partner ? partner.name : "";
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const [year, month, day] = dateString.split("-");
      return `${day}-${month}-${year}`;
    },

    async download() {
      this.downloadLoader = true;
      const queryString = this.objectToQueryParams({ ...this.form });

      try {
        const res = await this.$axios.get(
          `/download_postal_fee_report?${queryString}`,
          { responseType: "blob" },
        );

        const blob = new Blob([res.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Postal Requests Report.csv");

        document.body.appendChild(link);
        link.click();
        this.downloadLoader = false;
      } catch (error) {
        console.log(error);
        this.downloadLoader = false;
      }
    },
    edit(item) {
      console.log(item);
      this.requestPayment = { ...item };
      this.amountChange = true;
    },
  },
};
</script>
